import { createReducer } from '../../../store.model';

import {
    ActionType,
    ContributionGridReset,
    ContributionReload,
    DRScorecardGridReset,
    ExpandAll,
    ExpandMetricColumns,
    ExpandScorecardMetricColumns,
    FilterDirectReportContributionGrid,
    FilterDirectReportGridPillar,
    FilterDirectReportRankGrid,
    FilterDirectReportScorecardGrid,
    RankGridReset,
    RankReload,
    ScorecardReload,
    SortDirectReportContributionGrid,
    SortDirectReportRankGrid,
    SortDirectReportScorecardGrid,
} from './team-grid.action';
import {
    expandAllColumns,
    expandMetricColumns,
    filterDirectReportContributionGrid,
    filterDirectReportRankGrid,
    filterDirectReportScorecardGrid,
    filterGridPillar,
    sortDirectReportContributionGrid,
    sortDirectReportRankGrid,
    sortDirectReportScorecardGrid,
    syncContributionGrid,
    syncRankGrid,
    syncScorecardGrid,
} from './team-grid.function';
import {
    defaultTeamGridState,
    DirectReportContributionGrid,
    DirectReportRankGrid,
    DirectReportScorecardGrid,
    TeamGridState,
} from './team-grid.state';

const { reducer, builder } = createReducer<ActionType, TeamGridState>(
    defaultTeamGridState(),
);

builder.add<ContributionReload>(
    ActionType.DIRECTREPORT_CONTRIBUTION_GRID__RELOAD,
    (state, action) => {
        const nextContributionGrid = syncContributionGrid(
            action.payload.tplConfig,
            action.payload.header,
            action.payload.directReportContributionGrid || [],
            state.directReportContributionGrid.sortDefs,
        );

        const prevContributionGrid: DirectReportContributionGrid = {
            scorecardId: action.payload.header.scorecardId,
            superGroupName: action.payload.header.groupName,
            columns: [...nextContributionGrid.columns],
            rowsTop: [...nextContributionGrid.rowsTop],
            rows: [...nextContributionGrid.rows],
            sortDefs: [...nextContributionGrid.sortDefs],
            filterDef: [...nextContributionGrid.filterDef],
            filterPillar: [...nextContributionGrid.filterPillar],
            defaultColumnIdOrder: [
                ...nextContributionGrid.defaultColumnIdOrder,
            ],
        };

        return {
            ...state,
            mode: action.payload.tplConfig.mode,
            directReportContributionGrid: nextContributionGrid,
            directReportContributionGridExpandedAll: false,
            directReportPrevContributionGrid: prevContributionGrid,
        };
    },
);

builder.add<ExpandAll>(ActionType.DIRECTREPORT_EXPAND_ALL, (state, action) => {
    expandAllColumns(
        action.payload.expand,
        state.directReportContributionGrid.columns,
    );

    return {
        ...state,
        directReportContributionGrid: { ...state.directReportContributionGrid },
        directReportContributionGridExpandedAll: action.payload.expand,
    };
});

builder.add<ExpandMetricColumns>(
    ActionType.DIRECTREPORT_EXPAND_METRICCOLUMNS,
    (state, action) => {
        expandMetricColumns(
            action.payload.columnId,
            state.directReportContributionGrid.columns,
        );

        return {
            ...state,
            directReportContributionGrid: {
                ...state.directReportContributionGrid,
            },
        };
    },
);

builder.add<RankReload>(
    ActionType.DIRECTREPORT_RANK_GRID__RELOAD,
    (state, action) => {
        const nextDirectReportRankGrid = syncRankGrid(
            action.payload.tplConfig,
            action.payload.header,
            action.payload.directReportRankGrid || [],
            state.directReportRankGrid.sortDefs,
        );

        const prevRankGrid: DirectReportRankGrid = {
            scorecardId: action.payload.header.scorecardId,
            columns: [...nextDirectReportRankGrid.columns],
            rows: [...nextDirectReportRankGrid.rows],
            sortDefs: [...nextDirectReportRankGrid.sortDefs],
            filterDef: [...nextDirectReportRankGrid.filterDef],
        };

        return {
            ...state,
            mode: action.payload.tplConfig.mode,
            directReportRankGrid: nextDirectReportRankGrid,
            directReportPrevRankGrid: prevRankGrid,
        };
    },
);

builder.add<SortDirectReportRankGrid>(
    ActionType.DIRECTREPORT_RANK_GRID__SORT,
    (state, action) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { rows: nextRows, sortDefs: nextSortDefs } =
            sortDirectReportRankGrid(
                action.payload.columnId,
                action.payload.keepOrder,
                state.directReportRankGrid.columns || [],
                state.directReportRankGrid.rows,
                state.directReportRankGrid.sortDefs,
            );

        return {
            ...state,
            directReportRankGrid: {
                ...state.directReportRankGrid,
                sortDefs: nextSortDefs,
            },
        };
    },
);

builder.add<FilterDirectReportRankGrid>(
    ActionType.DIRECTREPORT_RANK_GRID__FILTER,
    (state, action) => {
        const {
            columns: nextColumns,
            rows: nextRows,
            sortDefs: nextSortDefs,
            filterDefs: nextFilterDef,
        } = filterDirectReportRankGrid(
            state.directReportRankGrid.columns || [],
            state.directReportRankGrid.rows || [],
            state.directReportRankGrid.sortDefs,
            action.payload.payload,
            state.directReportRankGrid.filterDef,
        );

        return {
            ...state,
            directReportRankGrid: {
                ...state.directReportRankGrid,
                columns: nextColumns,
                rows: nextRows,
                sortDefs: nextSortDefs,
                filterDef: nextFilterDef,
            },
        };
    },
);

builder.add<FilterDirectReportScorecardGrid>(
    ActionType.DIRECTREPORT_SCORECARD_GRID__FILTER,
    (state, action) => {
        const {
            rows: nextRows,
            sortDefs: nextSortDefs,
            filterDefs: nextFilterDef,
        } = filterDirectReportScorecardGrid(
            state.directReportScorecardGrid.rows,
            state.directReportScorecardGrid.columns,
            state.directReportScorecardGrid.sortDefs,
            action.payload.payload,
            state.directReportScorecardGrid.filterDef,
        );

        return {
            ...state,
            directReportScorecardGrid: {
                ...state.directReportScorecardGrid,
                rows: nextRows,
                sortDefs: nextSortDefs,
                filterDef: nextFilterDef,
            },
        };
    },
);

builder.add<SortDirectReportContributionGrid>(
    ActionType.DIRECTREPORT_CONTRIBUTION_GRID__SORT,
    (state, action) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            rowsTop: nextRowsTop,
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            rows: nextRows,
            sortDefs: nextSortDefs,
        } = sortDirectReportContributionGrid(
            action.payload.columnId,
            state.directReportContributionGrid.columns,
            state.directReportContributionGrid.rowsTop,
            state.directReportContributionGrid.rows,
            state.directReportContributionGrid.sortDefs,
        );
        return {
            ...state,
            directReportContributionGrid: {
                ...state.directReportContributionGrid,
                sortDefs: nextSortDefs,
            },
        };
    },
);

builder.add<FilterDirectReportContributionGrid>(
    ActionType.DIRECTREPORT_CONTRIBUTION_GRID__FILTER,
    (state, action) => {
        const {
            rowsTop: nextRowsTop,
            rows: nextRows,
            sortDefs: nextSortDefs,
            filterDefs: nextFilterDef,
        } = filterDirectReportContributionGrid(
            state.directReportContributionGrid.rowsTop,
            state.directReportContributionGrid.rows,
            state.directReportContributionGrid.sortDefs,
            action.payload.payload,
            state.directReportContributionGrid.filterDef,
        );

        return {
            ...state,
            directReportContributionGrid: {
                ...state.directReportContributionGrid,
                rowsTop: nextRowsTop,
                rows: nextRows,
                sortDefs: nextSortDefs,
                filterDef: nextFilterDef,
            },
        };
    },
);

builder.add<FilterDirectReportGridPillar>(
    ActionType.DIRECTREPORT_CONTRIBUTION_GRID_PILLAR__FILTER,
    (state, action) => {
        const { columns: nextColumns, filterPillars: nextFilterPillar } =
            filterGridPillar(
                action.payload.payload,
                state.directReportContributionGrid.columns,
                state.directReportContributionGrid.filterPillar,
                state.directReportContributionGrid.defaultColumnIdOrder,
            );

        return {
            ...state,
            directReportContributionGrid: {
                ...state.directReportContributionGrid,
                columns: nextColumns,
                filterPillar: nextFilterPillar,
            },
        };
    },
);

builder.add<FilterDirectReportGridPillar>(
    ActionType.DIRECTREPORT_SCORECARD_GRID_PILLAR__FILTER,
    (state, action) => {
        const { columns: nextColumns, filterPillars: nextFilterPillar } =
            filterGridPillar(
                action.payload.payload,
                state.directReportScorecardGrid.columns,
                state.directReportScorecardGrid.filterPillar,
                state.directReportScorecardGrid.defaultColumnIdOrder,
            );

        return {
            ...state,
            directReportScorecardGrid: {
                ...state.directReportScorecardGrid,
                columns: nextColumns,
                filterPillar: nextFilterPillar,
            },
        };
    },
);

builder.add<ExpandScorecardMetricColumns>(
    ActionType.DIRECTREPORT_EXPAND_SCORECARDMETRICCOLUMNS,
    (state, action) => {
        expandMetricColumns(
            action.payload.columnId,
            state.directReportScorecardGrid.columns,
        );

        return {
            ...state,
            directReportScorecardGrid: { ...state.directReportScorecardGrid },
        };
    },
);

builder.add<ExpandAll>(
    ActionType.DIRECTREPORT_SCORECARD_EXPAND_ALL,
    (state, action) => {
        expandAllColumns(
            action.payload.expand,
            state.directReportScorecardGrid.columns,
        );

        return {
            ...state,
            directReportScorecardGrid: { ...state.directReportScorecardGrid },
            directReportScorecardGridExpandedAll: action.payload.expand,
        };
    },
);

builder.add<ScorecardReload>(
    ActionType.DIRECTREPORT_SCORECARD_GRID__RELOAD,
    (state, action) => {
        const nextScorecardGrid = syncScorecardGrid(
            action.payload.tplConfig,
            action.payload.header,
            action.payload.directReportScorecardGrid || [],
            state.directReportScorecardGrid.sortDefs,
        );

        const prevContributionGrid: DirectReportScorecardGrid = {
            scorecardId: action.payload.header.scorecardId,
            superGroupName: action.payload.header.groupName,
            columns: [...nextScorecardGrid.columns],
            rows: [...nextScorecardGrid.rows],
            sortDefs: [...nextScorecardGrid.sortDefs],
            filterDef: [...nextScorecardGrid.filterDef],
            filterPillar: [...nextScorecardGrid.filterPillar],
            defaultColumnIdOrder: [...nextScorecardGrid.defaultColumnIdOrder],
        };

        return {
            ...state,
            mode: action.payload.tplConfig.mode,
            directReportScorecardGrid: nextScorecardGrid,
            directReportScorecardGridExpandedAll: false,
            directReportPrevScorecardGrid: prevContributionGrid,
        };
    },
);

builder.add<RankGridReset>(
    ActionType.DIRECT_REPORT_RANK_GRID_RESET,
    (state, action) => {
        return {
            ...state,
            directReportRankGrid: {
                ...state.directReportPrevRankGrid,
                columns: [...state.directReportPrevRankGrid.columns],
                rows: [...state.directReportPrevRankGrid.rows],
                sortDefs: [],
            },
        };
    },
);

builder.add<ContributionGridReset>(
    ActionType.DIRECT_REPORT_CONTRIBUTION_GRID_RESET,
    (state, action) => {
        return {
            ...state,
            directReportContributionGrid: {
                ...state.directReportPrevContributionGrid,
                columns: [...state.directReportPrevContributionGrid.columns],
                rows: [...state.directReportPrevContributionGrid.rows],
            },
        };
    },
);

builder.add<DRScorecardGridReset>(
    ActionType.DIRECT_REPORT_SCORECARD_GRID_RESET,
    (state, action) => {
        return {
            ...state,
            directReportScorecardGrid: {
                ...state.directReportPrevScorecardGrid,
                columns: [...state.directReportPrevScorecardGrid.columns],
                rows: [...state.directReportPrevScorecardGrid.rows],
                sortDefs: [],
            },
        };
    },
);

builder.add<SortDirectReportScorecardGrid>(
    ActionType.DIRECTREPORT_SCORECARD_GRID__SORT,
    (state, action) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { rows: nextRows, sortDefs: nextSortDefs } =
            sortDirectReportScorecardGrid(
                action.payload.columnId,
                action.payload.keepOrder,
                state.directReportScorecardGrid.columns,
                state.directReportScorecardGrid.rows,
                state.directReportScorecardGrid.sortDefs,
            );

        return {
            ...state,
            directReportScorecardGrid: {
                ...state.directReportScorecardGrid,
                sortDefs: nextSortDefs,
            },
        };
    },
);

export default {
    teamGrid: reducer,
};
